import React from 'react';
import Alert, { AlertProps } from '@amzn/awsui-components-core/alert';
import { ArrowOutwardSmallIcon } from '../../utils/inlineIcons';
import './NotificationBanner.scss';

/**
 * NotificationBanner - Lightweight Cloudscape Alert wrapper for page-level notifications
 *
 * Types: info, error, warning, success, tip
 * Sizes: default (1024px max-width), max (1200px max-width)
 */

export type NotificationBannerType = 'info' | 'error' | 'warning' | 'success' | 'tip';
export type NotificationBannerSize = 'default' | 'max';

/**
 * @polySemanticUsage About this block: a contextual inline banner that surfaces relevant context,
 * guidance, feedback, or time-sensitive callouts within the content flow. Variants: "Info" for
 * neutral context-setting (eligibility details, program requirements, good-to-know information);
 * "Tip" for advisory best practices and insider guidance in an editorial voice; "Success" to
 * confirm a user action completed successfully (placed within or adjacent to form blocks);
 * "Error" to communicate that an action did not complete and what to fix; "Warning" for
 * time-sensitive or urgency-driven callouts about upcoming changes, approaching deadlines, or
 * expiring opportunities.
 */
export interface NotificationBannerProps extends Omit<AlertProps, 'type' | 'action' | 'dismissible' | 'onDismiss' | 'style' | 'i18nStrings'> {
  /**
   * Notification type — determines icon and color scheme
   * @polyLabel Alert Type
   * @polyOptions Info=info,Tip=tip,Success=success,Warning=warning,Error=error
   */
  type?: NotificationBannerType;
  /**
   * Size variant — controls max-width of the alert
   * @polyOptions Default=default,Max=max
   */
  size?: NotificationBannerSize;
  /** Header text */
  header?: string;
  /** Description text below the header */
  description?: string;
  /**
   * Action button text
   * @polyLabel Action Text
   */
  actionText?: string;
  /**
   * Action button URL (renders as anchor)
   * @polyType Url
   * @polyLabel Action Link
   */
  actionHref?: string;
  /**
   * Action button click handler (used when actionHref is not provided)
   * @polyIgnore
   */
  onActionClick?: () => void;
  /**
   * Action button icon
   * @polyOptions Arrow Outward=arrow-outward,None=none
   */
  actionIcon?: 'arrow-outward' | 'none';
  /** Whether the notification can be dismissed */
  dismissible?: boolean;
  /**
   * Callback when dismissed
   * @polyIgnore
   */
  onDismiss?: () => void;
  /**
   * ARIA label for the dismiss button
   * @polyIgnore
   */
  dismissAriaLabel?: string;
  /**
   * Children content (alternative to description prop)
   * @polyIgnore
   */
  children?: React.ReactNode;
}

/** Map our 'tip' type to Cloudscape's 'info' since Cloudscape has no native tip type */
const CLOUDSCAPE_TYPE_MAP: Record<NotificationBannerType, AlertProps['type']> = {
  info: 'info',
  error: 'error',
  warning: 'warning',
  success: 'success',
  tip: 'info',
};

/**
 * Per-type style overrides via Cloudscape's official `style` prop API. Alert writes
 * `root` values as real inline styles; `icon.color` travels through a Cloudscape
 * custom property, so NotificationBanner.scss pins it again on a stable selector.
 */
const alertStyleFor = (type: NotificationBannerType): AlertProps['style'] => ({
  root: {
    background: `var(--poly-color-notification-${type}-bg)`,
    borderColor: `var(--poly-color-notification-${type}-border)`,
    borderWidth: 'var(--poly-size-alert-borderWidth)',
    borderRadius: 'var(--poly-border-radius-notification)',
  },
  icon: { color: `var(--poly-color-notification-${type}-icon)` },
});

const ALERT_STYLES: Record<NotificationBannerType, AlertProps['style']> = {
  info: alertStyleFor('info'),
  error: alertStyleFor('error'),
  warning: alertStyleFor('warning'),
  success: alertStyleFor('success'),
  tip: alertStyleFor('tip'),
};

const ActionButton: React.FC<{ text: string; href?: string; onClick?: () => void; icon?: 'arrow-outward' | 'none' }> = ({ text, href, onClick, icon }) => {
  const showIcon = icon !== 'none';
  const iconEl = showIcon ? <ArrowOutwardSmallIcon className="notification-banner__action-icon" aria-hidden="true" /> : null;

  if (href) {
    return (
      <a className="notification-banner__action-btn" href={href}>
        <span className="notification-banner__action-text">{text}</span>
        {iconEl}
      </a>
    );
  }

  return (
    <button className="notification-banner__action-btn" type="button" onClick={onClick}>
      <span className="notification-banner__action-text">{text}</span>
      {iconEl}
    </button>
  );
};

export const NotificationBanner: React.FC<NotificationBannerProps> = ({
  type = 'info',
  size = 'default',
  header,
  description,
  actionText,
  actionHref,
  onActionClick,
  actionIcon,
  dismissible = false,
  onDismiss,
  dismissAriaLabel = 'Dismiss notification',
  children,
  ...alertProps
}) => {
  const cloudscapeType = CLOUDSCAPE_TYPE_MAP[type];
  const actionSlot = actionText ? <ActionButton text={actionText} href={actionHref} onClick={onActionClick} icon={actionIcon} /> : undefined;
  const headerSlot = header ? <span className="notification-banner__header">{header}</span> : undefined;
  const body = children || description;
  const rootClass = [
    'notification-banner',
    `notification-banner--${size}`,
    `notification-banner--${type}`,
    body ? 'notification-banner--has-body' : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClass}>
      <Alert
        {...alertProps}
        type={cloudscapeType}
        header={headerSlot}
        dismissible={dismissible}
        onDismiss={onDismiss}
        action={actionSlot}
        style={ALERT_STYLES[type]}
        i18nStrings={{ dismissAriaLabel }}
      >
        {body}
      </Alert>
    </div>
  );
};
